import { openDB, type IDBPDatabase } from 'idb';
import { DEFAULT_STATE, type GameStateData } from './GameState';

const DB_NAME = 'forlagshuset';
const DB_VERSION = 1;
const STORE_NAME = 'state';
const STATE_KEY = 'save';
const LEGACY_STORAGE_KEY = 'forlagshuset:save:v1';

let dbPromise: Promise<IDBPDatabase> | null = null;

function isIndexedDbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function getDb(): Promise<IDBPDatabase> {
  if (!isIndexedDbAvailable()) {
    return Promise.reject(new Error('indexedDB ikke tilgjengelig'));
  }
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    });
  }
  return dbPromise;
}

function defaultState(): GameStateData {
  return { ...DEFAULT_STATE };
}

function parseLegacyLocalStorage(): GameStateData | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GameStateData>;
    if (parsed.version !== 1) return null;
    return { ...DEFAULT_STATE, ...parsed, version: 1 };
  } catch {
    return null;
  }
}

function clearLegacyLocalStorage(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export async function loadSaveAsync(): Promise<GameStateData> {
  if (!isIndexedDbAvailable()) {
    const legacy = parseLegacyLocalStorage();
    return legacy ?? defaultState();
  }
  try {
    const db = await getDb();
    const raw = (await db.get(STORE_NAME, STATE_KEY)) as
      | Partial<GameStateData>
      | undefined;
    if (raw && raw.version === 1) {
      return { ...DEFAULT_STATE, ...raw, version: 1 };
    }

    const legacy = parseLegacyLocalStorage();
    if (legacy) {
      await db.put(STORE_NAME, legacy, STATE_KEY);
      clearLegacyLocalStorage();
      return legacy;
    }
    return defaultState();
  } catch {
    const legacy = parseLegacyLocalStorage();
    return legacy ?? defaultState();
  }
}

export async function persistSaveAsync(state: GameStateData): Promise<void> {
  if (!isIndexedDbAvailable()) return;
  try {
    const db = await getDb();
    await db.put(STORE_NAME, state, STATE_KEY);
  } catch {
    /* quota fullt eller bruker har private-mode — feiler stille i MVP */
  }
}

export async function clearSaveAsync(): Promise<void> {
  clearLegacyLocalStorage();
  if (!isIndexedDbAvailable()) return;
  try {
    const db = await getDb();
    await db.delete(STORE_NAME, STATE_KEY);
  } catch {
    /* ignore */
  }
}

export function _resetDbCacheForTest(): void {
  dbPromise = null;
}
